import { useDisclosure, useToast } from '@chakra-ui/react';
import { router } from '@inertiajs/react';
import { mdiClose, mdiLink, mdiLinkEdit, mdiLinkOff, mdiMapCheck, mdiMapMarker, mdiPencil } from '@mdi/js';
import { PlaceListItem, Visit } from '@/pages/types';
import { DeletePlaceListItemModal } from '@/pages/place-list-items/components/DeletePlaceListItemModal';
import { VisitInfoCard } from '@/pages/visits/components/VisitInfoCard';
import { PlaceDetail } from '@/pages/places/components/PlaceDetail';
import { VisitSelectionModal } from './VisitSelectionModal';

type PlaceListItemDetailProps = {
    placeListItem: PlaceListItem;
    visits?: Visit[];
    canUpdate: boolean;
};

export function PlaceListItemDetail({ placeListItem, visits, canUpdate }: PlaceListItemDetailProps) {
    const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
    const {
        isOpen: isVisitSelectionOpen,
        onOpen: onVisitSelectionOpen,
        onClose: onVisitSelectionClose,
    } = useDisclosure();
    const toast = useToast();

    const handleLinkVisit = (visit: Visit) => {
        router.put(
            route('place-list-items.update', { place_list_item: placeListItem, fromSection: 'list' }),
            { linked_visit_id: visit.id },
            {
                preserveScroll: true,
                onSuccess: () => {
                    toast({
                        title: 'Visit linked successfully',
                        status: 'success',
                        position: 'top',
                        variant: 'left-accent',
                    });
                },
                onError: () => {
                    toast({
                        title: 'Error linking visit',
                        description: 'An error occurred while linking the visit.',
                        status: 'error',
                        isClosable: true,
                    });
                },
            }
        );
    };

    const handleUnlinkVisit = () => {
        router.put(
            route('place-list-items.update', { place_list_item: placeListItem, fromSection: 'list' }),
            { linked_visit_id: null },
            {
                preserveScroll: true,
                onSuccess: () => {
                    toast({
                        title: 'Visit unlinked successfully',
                        status: 'success',
                        position: 'top',
                        variant: 'left-accent',
                    });
                },
                onError: () => {
                    toast({
                        title: 'Error unlinking visit',
                        description: 'An error occurred while unlinking the visit.',
                        status: 'error',
                        isClosable: true,
                    });
                },
            }
        );
    };

    return (
        <div className='py-4'>
            <PlaceDetail
                place={placeListItem.place}
                notes={placeListItem.notes}
                menu={
                    <PlaceDetail.Menu>
                        <PlaceDetail.MenuItem
                            href={route('places.show', { place: placeListItem.place })}
                            iconName={mdiMapMarker}
                        >
                            Open place
                        </PlaceDetail.MenuItem>
                        {placeListItem.linked_visit && (
                            <>
                                <PlaceDetail.MenuItem
                                    href={route('visits.show', { visit: placeListItem.linked_visit })}
                                    iconName={mdiMapCheck}
                                >
                                    Open visit
                                </PlaceDetail.MenuItem>
                            </>
                        )}
                        {canUpdate && visits && visits.length > 0 && (
                            <>
                                {placeListItem.linked_visit ? (
                                    <>
                                        <PlaceDetail.MenuDivider />
                                        <PlaceDetail.MenuItem onClick={onVisitSelectionOpen} iconName={mdiLinkEdit}>
                                            Change linked visit
                                        </PlaceDetail.MenuItem>
                                        <PlaceDetail.MenuItem onClick={handleUnlinkVisit} iconName={mdiLinkOff}>
                                            Unlink visit
                                        </PlaceDetail.MenuItem>
                                    </>
                                ) : (
                                    <>
                                        <PlaceDetail.MenuDivider />
                                        <PlaceDetail.MenuItem onClick={onVisitSelectionOpen} iconName={mdiLink}>
                                            Link a visit
                                        </PlaceDetail.MenuItem>
                                    </>
                                )}
                            </>
                        )}
                        {canUpdate && (
                            <>
                                <PlaceDetail.MenuDivider />
                                <PlaceDetail.MenuItem
                                    href={route('place-list-items.edit', {
                                        place_list_item: placeListItem,
                                        fromSection: 'list',
                                    })}
                                    iconName={mdiPencil}
                                >
                                    Edit list note
                                </PlaceDetail.MenuItem>
                                <PlaceDetail.MenuItem onClick={onDeleteModalOpen} iconName={mdiClose} variant='danger'>
                                    Remove from list
                                </PlaceDetail.MenuItem>
                            </>
                        )}
                    </PlaceDetail.Menu>
                }
            />

            {/* TODO: show prompt to save place if place is not in Planning or Visited */}

            {placeListItem.linked_visit && <VisitInfoCard visit={placeListItem.linked_visit} className='mt-6' />}

            {canUpdate && (
                <DeletePlaceListItemModal
                    isOpen={isDeleteModalOpen}
                    onClose={onDeleteModalClose}
                    placeListItem={placeListItem}
                />
            )}

            {visits && visits.length > 0 && (
                // TODO: page-level modal instead of per-item
                <VisitSelectionModal
                    isOpen={isVisitSelectionOpen}
                    onClose={onVisitSelectionClose}
                    visits={visits}
                    onSelectVisit={handleLinkVisit}
                />
            )}
        </div>
    );
}
